/** @format */

import React, {useState, useEffect, useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Button} from '@sttark/design-system';
import pixelmatch from 'pixelmatch';
import {PNG} from 'pngjs/browser';
import SuperAgent from 'superagent';

// Constants
const VIEW_MODES = {
    NEW: 'new',
    ORIGINAL: 'original',
    DIFFERENCE: 'difference'
};

// CSS classes for active/inactive buttons
const ACTIVE_BUTTON_CLASS = 'btn-success';
const INACTIVE_BUTTON_CLASS = 'btn-light';

/**
 * QuickImagePreview component for comparing two images and showing their differences
 *
 * @param {Object} props - Component props
 * @param {string} props.thumbKey1 - S3 key for the original image
 * @param {string} props.thumbKey2 - S3 key for the new image
 * @param {number} props.width - Original width of the image
 * @param {number} props.height - Original height of the image
 * @returns {React.ReactElement}
 */
export const QuickImagePreview = ({
    onRemove,
    thumbKey1,
    newFileName,
    thumbKey2,
    width,
    height,
    filename
}) => {
    // State
    const [viewMode, setViewMode] = useState(VIEW_MODES.NEW);
    const [originalImage, setOriginalImage] = useState(null);
    const [newImage, setNewImage] = useState(null);
    const [diffImage, setDiffImage] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Calculate display dimensions
    const displayWidth = 400;
    const displayHeight = useMemo(() => {
        return width > 0 ? Math.max(100, Math.floor((displayWidth * height) / width)) : 100;
    }, [width, height, displayWidth]);

    /**
     * Fetches image data from S3 using a presigned URL
     *
     * @param {string} s3key - The S3 key of the image
     * @returns {Promise<ArrayBuffer|boolean>} - The image data or false if there was an error
     */
    const fetchImageData = useCallback(
        async s3key => {
            if (!s3key) return false;

            try {
                // Get presigned URL
                const signingUrl = `/account/designs/get-presigned-url?key=${s3key}`;
                const {
                    body: {url}
                } = await SuperAgent.get(signingUrl).set('accept', 'application/json');

                // Process image with Filestack
                const processingUrl = `${process.env.FILESTACK_CDN_URL}/${process.env.FILESTACK_KEY}/resize=width:${displayWidth},height:${displayHeight},fit:crop/${url}`;

                // Fetch image data as array buffer
                const {body: imageData} =
                    await SuperAgent.get(processingUrl).responseType('arraybuffer');

                return imageData;
            } catch (err) {
                console.error('Error fetching image:', err);
                setError('Failed to load image');
                return false;
            }
        },
        [displayWidth, displayHeight]
    );

    /**
     * Creates an object URL from image data
     *
     * @param {ArrayBuffer} imageData - The image data
     * @returns {string} - The object URL
     */
    const createImageUrl = useCallback(imageData => {
        return URL.createObjectURL(new Blob([imageData], {type: 'image/png'}));
    }, []);

    /**
     * Parses PNG data using pngjs
     *
     * @param {ArrayBuffer} imageData - The image data
     * @returns {Promise<Object>} - The parsed PNG data
     */
    const parsePngData = useCallback(imageData => {
        return new Promise(resolve => {
            new PNG().parse(imageData, (err, data) => resolve(data));
        });
    }, []);

    /**
     * Generates a difference image between two PNG images
     *
     * @param {Object} img1Data - The first image data
     * @param {Object} img2Data - The second image data
     * @returns {string} - The object URL of the difference image
     */
    const generateDiffImage = useCallback(
        (img1Data, img2Data) => {
            const canvas = document.createElement('canvas');
            canvas.width = displayWidth;
            canvas.height = displayHeight;
            const context = canvas.getContext('2d');

            const diffData = context.createImageData(displayWidth, displayHeight);

            pixelmatch(img1Data.data, img2Data.data, diffData.data, displayWidth, displayHeight, {
                threshold: 0.1
            });

            context.putImageData(diffData, 0, 0);

            return canvas.toDataURL('image/png');
        },
        [displayWidth, displayHeight]
    );

    // Load images when thumbKeys change
    useEffect(() => {
        const loadImages = async () => {
            setIsLoading(true);
            setError(null);

            try {
                // Fetch image data
                const [img1Data, img2Data] = await Promise.all([
                    fetchImageData(thumbKey1),
                    fetchImageData(thumbKey2)
                ]);

                // Create image URLs
                if (img1Data) {
                    setOriginalImage(createImageUrl(img1Data));
                }

                if (img2Data) {
                    setNewImage(createImageUrl(img2Data));
                }

                // Generate diff image if both images are available
                if (img1Data && img2Data) {
                    const [parsedImg1, parsedImg2] = await Promise.all([
                        parsePngData(img1Data),
                        parsePngData(img2Data)
                    ]);

                    const diffUrl = generateDiffImage(parsedImg1, parsedImg2);
                    setDiffImage(diffUrl);
                }
            } catch (err) {
                console.error('Error processing images:', err);
                setError('Failed to process images');
            } finally {
                setIsLoading(false);
            }
        };

        if (thumbKey1 || thumbKey2) {
            loadImages();
        }

        // Cleanup function to revoke object URLs
        return () => {
            if (originalImage) URL.revokeObjectURL(originalImage);
            if (newImage) URL.revokeObjectURL(newImage);
        };
    }, [thumbKey1, thumbKey2, fetchImageData, createImageUrl, parsePngData, generateDiffImage]);

    // Handle view mode change
    const handleViewModeChange = useCallback(mode => {
        setViewMode(mode);
    }, []);

    // Render loading state
    if (isLoading) {
        return (
            <div className="d-flex flex-column align-items-center">
                <div className="p-4 text-center">Loading images...</div>
            </div>
        );
    }

    // Render error state
    if (error) {
        return (
            <div className="d-flex flex-column align-items-center">
                <div className="p-4 text-center text-danger">{error}</div>
            </div>
        );
    }

    // Determine which image to show based on view mode
    const currentImage = (() => {
        switch (viewMode) {
            case VIEW_MODES.ORIGINAL:
                return originalImage;
            case VIEW_MODES.DIFFERENCE:
                return diffImage;
            case VIEW_MODES.NEW:
            default:
                return newImage;
        }
    })();

    // Determine download link based on view mode
    const downloadLink = (() => {
        switch (viewMode) {
            case VIEW_MODES.ORIGINAL:
                return thumbKey1 ? `/account/designs/download-design?key=${thumbKey1}` : null;
            case VIEW_MODES.NEW:
                return thumbKey2 ? `/account/designs/download-design?key=${thumbKey2}` : null;
            default:
                return null;
        }
    })();

    return (
        <div className="d-flex flex-column align-items-center" data-testid="quick-image-preview">
            <div
                className="w-100 p-4 d-flex align-items-center justify-content-between gap-4"
                style={{background: '#e0dfe5'}}>
                <p className="m-0 p-0 text-wrap">
                    {viewMode === VIEW_MODES.NEW ? newFileName : filename}
                </p>
                <button
                    onClick={onRemove}
                    className="p-0 d-flex align-items-center justify-content-center gap-2">
                    <i className="fa fa-trash color-alert-negative" />
                    <u className="color-alert-negative">Remove</u>
                </button>
            </div>
            <div className="position-relative w-100">
                {currentImage && (
                    <a
                        href={downloadLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={downloadLink ? 'd-block' : 'd-none'}>
                        <img
                            src={currentImage}
                            alt={`Image ${viewMode}`}
                            width={displayWidth}
                            height={displayHeight}
                            className="d-block max-w-100 w-100 p-4"
                            style={{
                                objectFit: 'contain',
                                objectPosition: 'center',
                                maxHeight: 300,
                                background: '#ecebf1'
                            }}
                        />
                    </a>
                )}
                {viewMode === VIEW_MODES.DIFFERENCE && diffImage && (
                    <img
                        src={diffImage}
                        alt="Difference"
                        style={{
                            objectFit: 'contain',
                            objectPosition: 'center',
                            maxHeight: 300,
                            background: '#ecebf1'
                        }}
                        width={displayWidth}
                        height={displayHeight}
                        className="d-block max-w-100 w-100 p-4"
                    />
                )}
            </div>

            {(thumbKey1 || thumbKey2) && (
                <div className="d-flex align-items-center justify-content-center w-100">
                    <button
                        onClick={() => handleViewModeChange(VIEW_MODES.NEW)}
                        style={{color: '#39ac6e'}}
                        className="btn btn-link w-100 bg-transparent p-0 text-center"
                        disabled={!newImage}>
                        <u>New</u>
                    </button>
                    <button
                        onClick={() => handleViewModeChange(VIEW_MODES.ORIGINAL)}
                        style={{color: '#242424'}}
                        className="btn btn-link w-100 bg-transparent p-0 text-center"
                        disabled={!originalImage}>
                        <u>Original</u>
                    </button>
                    <button
                        onClick={() => handleViewModeChange(VIEW_MODES.DIFFERENCE)}
                        style={{color: '#fab347'}}
                        className="btn btn-link w-100 bg-transparent p-0 text-center"
                        disabled={!diffImage}>
                        <u>Difference</u>
                    </button>
                </div>
            )}
        </div>
    );
};
